import {
  StudioResourceTypeEnum,
  Tenancy_Node_Type_Enum_Ps_Enum,
} from '@pypestream/api-services';

import { defaultRowData, type TableNode } from '../../../stores/table-store';
import type { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';

/**
 * The platform's built-in action nodes as Resource Library items (Bobcat
 * status check, 2026-09-29: "pull the system action nodes into that list").
 *
 * Built from the live native command list (`tableStore.actionNodeNames`,
 * system ∪ library — see `stores/utils/action-node-names.ts`), so a command
 * the backend adds shows up without a change here. Each is one Action node
 * running that command; being native, it needs no script upload
 * (`scriptNamesFor` leaves it out).
 */

/** What each built-in does, in a line — shown on its card. */
const DESCRIPTIONS: Record<string, string> = {
  AsyncSysGetRAGSearchResponse:
    'Search the knowledge base and return an answer grounded in it.',
  AsyncSysGoogleAICompletion: 'Get a completion from the Gemini model.',
  SysAssignVariable: 'Set one or more variables to a value.',
  SysGenerateUniqueID: 'Generate a unique ID and store it in a variable.',
  SysJSONToGlobalVars: 'Unpack a JSON object into global variables.',
  SysLimitCounter:
    'Count passes through a node and branch once a limit is hit.',
  SysMultiMatchRouting: 'Route on several conditions at once.',
  SysNodeReturn: 'Return to the node that called into this flow.',
  SysSetEnv: 'Set the environment values the solution runs with.',
  SysShowMetadata: 'Show the conversation’s metadata, for debugging.',
  SysSupervisorGetContext: 'Collect the context the Supervisor routes on.',
  SysSupervisorProcessSelection: 'Act on the agent the Supervisor picked.',
  SysSupervisorRouting: 'Route the conversation to the right agent.',
  SysVariableReset: 'Clear variables back to empty.',
  AsyncSysStartWorkflow: 'Start a workflow in the background.',
  SysSendNoticeMessage: 'Send a notice message to the user.',
  SysGetAsynchronousFunctionOutput:
    'Read the result of an action node that ran in the background.',
  AsyncSysCallExternalAPIWithCallback:
    'Call an external API and continue when it answers.',
  AsyncSysCallActionNodeWithCallback:
    'Run another action node and continue when it finishes.',
  ABTesting: 'Split conversations between paths for an A/B test.',
  Back: 'Go back to the previous node.',
  BotToPlatform: 'Hand the conversation to a live agent platform.',
  GetRichLabel: 'Read the label of the rich asset option the user picked.',
  Jump: 'Jump to another node.',
  OAuthGetAuthToken: 'Get an OAuth access token.',
  OAuthValidateAuthCode: 'Exchange an OAuth authorization code.',
  SwitchActiveLanguage: 'Switch the conversation’s language.',
  HandleBotError: 'Handle an error and route the user to recovery.',
};

export const SYSTEM_ACTION_ID_PREFIX = 'system-action:';

/**
 * One Library item per native command, skipping any the catalog already
 * offers under that name so nothing is listed twice.
 */
export const systemActionNodeItems = (
  commands: readonly string[],
  catalog: readonly ResourceLibItem[] = []
): ResourceLibItem[] => {
  const catalogued = new Set(
    catalog.flatMap((item) => [
      item.name ?? '',
      ...(item.data ?? []).map((row) => row.command ?? ''),
    ])
  );
  return [...new Set(commands)]
    .filter((command) => command && !catalogued.has(command))
    .sort((a, b) => a.localeCompare(b))
    .map((command) => {
      const description =
        DESCRIPTIONS[command] ?? 'Built-in platform function.';
      const row: TableNode = {
        ...defaultRowData,
        nodeType: Tenancy_Node_Type_Enum_Ps_Enum.A,
        nodeName: command,
        command,
        description,
        // Renumbered past the solution's highest on insert
        // (`prepareResourceLibRows`) when 1 is taken.
        nodeNumber: 1,
      };
      return {
        id: `${SYSTEM_ACTION_ID_PREFIX}${command}`,
        type: StudioResourceTypeEnum.ActionNode,
        name: command,
        description,
        data: [row],
      };
    });
};
